'use client';

import { Suspense, useEffect, type CSSProperties } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { CreateMatterProvider } from '@/lib/contexts/create-matter-context';
import { QueryProvider } from '@/lib/providers/query-provider';
import { UserProvider } from '@/lib/contexts/user-context';
import { UserProfileProvider } from '@/lib/providers/user-profile-provider';
import { FeatureFlagsProvider } from '@/lib/providers/feature-flags-provider';
import { ValeThemeProvider } from '@/components/theme/vale-theme-provider';
import { Toaster } from '@/components/ui/sonner';
import type { ThemeChoice } from '@/lib/utils/theme';
import { NavigationLoadingProvider } from '@/lib/contexts/navigation-loading-context';
import { FULL_SCREEN_SETUP_ROUTES } from '@/lib/constants/full-screen-routes';
import { isEligibleReturnPath } from '@/lib/utils/account-return-path';
import { AuthenticatedAppSidebar } from '@/components/app-sidebar/authenticated-app-sidebar';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { SIDEBAR_WIDTH } from '@/components/ui/sidebar/constants';
import { LayoutWrapper } from '@/components/layout-wrapper';
import { CTADialogProvider } from '@/lib/services/tasks/cta-dialog-context';
import { CorrespondenceWorkflowWrapper } from '@/components/workflows/correspondence-workflow-wrapper';
import { DevFeatureFlagToggle } from '@/components/dev-tools/dev-feature-flag-toggle';
import { UploadRecovery } from '@/components/ai-scribe/recording-flow/upload-recovery';
import { RecordingCompletionToaster } from '@/components/ai-scribe/recording-completion-toaster';
import { IntercomMessenger } from '@/components/intercom/intercom-messenger';
// LF-4086: the v2 activation gate is the permanent, data-driven activation page
// for all firms (v1 `TrialActivationGate` retired). It gates pending firms by
// default (opt-out per-firm) and active-free firms on opt-in.
import { ActivationV2Gate } from '@/components/auth/activation-v2-gate';
import {
  getMatterListNavigationActions,
  isMatterListId,
  getAccountNavigationActions
} from '@/lib/stores';
// The public **page** routes. `proxy.ts` reads the same module for its own
// gate, adding the API and framework prefixes that can never reach a rendered
// page — so the two can no longer drift (VALE-1361).
import { PUBLIC_PAGE_PATHS } from '@/lib/auth/public-paths';

/**
 * Tracks the active matter list view from the URL.
 * When the user is on `/?view=today` (or upcoming/anytime/all/archive),
 * this updates the navigation store so the back button on detail pages
 * can deterministically navigate to the correct view.
 */
function MatterListViewTracker() {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) return;
    // Extract view from pathname (e.g. '/today' → 'today')
    const view = pathname === '/' ? 'all' : pathname.replace('/', '');
    if (isMatterListId(view)) {
      getMatterListNavigationActions().setActiveMatterList(view);
    }
  }, [pathname]);

  return null;
}

/**
 * Tracks the last non-account path so the account sidebar back button
 * can return the user to where they came from.
 *
 * **Which paths qualify lives in `isEligibleReturnPath`** (VALE-1473), with the
 * correspondence-setup flow now excluded alongside `/integrations/`. It moved
 * out of here because the rule had grown a second member and nothing could
 * assert either of them.
 */
function AccountReturnPathTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!isEligibleReturnPath(pathname)) return;
    const search = searchParams.toString();
    const fullPath = search ? `${pathname}?${search}` : pathname;
    getAccountNavigationActions().setReturnPath(fullPath);
  }, [pathname, searchParams]);

  return null;
}

interface ProvidersWrapperProps {
  children: React.ReactNode;
  /**
   * The theme the SERVER read from the cookie for this request (VALE-520).
   * Threaded down from `app/layout.tsx` rather than re-read here so the value
   * is identical across SSR and hydration — `document.cookie` is not readable
   * on the server, and reading it during the client render would make the
   * provider's own props differ between the two.
   */
  initialThemeChoice?: ThemeChoice;
  /** The CSP nonce, for `next-themes`' inline anti-FOUC script. */
  nonce?: string;
}

export function ProvidersWrapper({
  children,
  initialThemeChoice,
  nonce
}: ProvidersWrapperProps) {
  const pathname = usePathname();

  // Check if current path is public
  const isPublicPath = PUBLIC_PAGE_PATHS.some((path) =>
    pathname?.startsWith(path)
  );

  // Details page has its own sidebar (IconRail + DetailsSidebar), don't wrap with app sidebar
  const isDetailsPage = pathname?.startsWith('/matters');

  // Account page has its own SidebarLayout, don't wrap with app sidebar
  const isAccountPage = pathname?.startsWith('/account');

  /*
    **The correspondence-setup flow renders full screen, with no app sidebar —
    and it is now two routes, so this is a SET** (VALE-1470).

    It was `pathname === '/onboarding'`, with a comment noting that any future
    `/onboarding/*` sub-route would fall through and get the sidebar. That was
    right for the hub and became wrong the moment VALE-1412 added the review
    route, which is the same flow and rendered inside the workspace shell while
    its first-sign-in twin did not.

    **Enumerated rather than a `startsWith`**, because the prefix is genuinely
    mixed: `/onboarding/get-started` is a dashboard hub and keeps its sidebar on
    purpose. A prefix match would take it away, and that member is the one this
    fix must not move. Adding a route to the flow means adding it here; the list
    is short, and `__tests__/full-screen-routes-registry.test.ts` reconciles it
    against the routes on disk so the next one cannot be forgotten the way this
    one was.
  */
  const isOnboardingFlow = FULL_SCREEN_SETUP_ROUTES.includes(pathname ?? '');

  // True for routes that render their own sidebar/layout shell — wrapping
  // them in the app sidebar here would nest two layouts. Matters' dashboard
  // views now render through the shared shell too (LF-5279), so they are no
  // longer skipped here.
  const skipSidebar = isDetailsPage || isAccountPage || isOnboardingFlow;

  // Always provide QueryClient - critical for preventing "No QueryClient set" errors
  return (
    <QueryProvider>
      {isPublicPath ? (
        // For public paths, only provide QueryClient and render children
        // directly. The theme provider is still mounted, forced light: the
        // public surfaces are out of scope for the dark theme (they are not
        // migrated onto semantic tokens), and there is no feature-flag provider
        // here to consult — reaching for one would fire an authenticated
        // `/profile` fetch for a reader with no session, which is the whole
        // reason this branch exists. Forcing light also fixes a latent bug:
        // with no provider at all, `ui/sonner.tsx`'s `useTheme()` fell back to
        // `system`, so a toast on the login page took its styling from the
        // visitor's OS preference.
        <ValeThemeProvider nonce={nonce} forceLight>
          {children}
          <Toaster />
        </ValeThemeProvider>
      ) : (
        // Single authed provider tree. The sidebar wrapper toggles inside —
        // swapping the entire branch (as we used to) caused React to remount
        // the provider subtree, which surfaced as a "Rendered more hooks"
        // error in the app router during /onboarding → /onboarding/get-started.
        <UserProfileProvider>
          <FeatureFlagsProvider>
            {/* Wraps the `Toaster`, because `ui/sonner.tsx` calls `useTheme()`
                and had been doing so against nothing since it was written
                (VALE-520). */}
            <ValeThemeProvider initialChoice={initialThemeChoice} nonce={nonce}>
              <Toaster />
              <UserProvider>
                <NavigationLoadingProvider>
                  <Suspense>
                    <MatterListViewTracker />
                    <AccountReturnPathTracker />
                  </Suspense>
                  <CreateMatterProvider>
                    <CTADialogProvider>
                      {/* Activation gate (LF-4086): the data-driven "Welcome to
                        Vale" page. Gates pending firms by default, exempted
                        per-firm via the `activationGateRequired` attribute
                        (VALE-873); every other firm renders the app untouched.
                        The second arm, active-free firms on `trial-gate-opt-in`,
                        was retired with its flag in VALE-1540. */}
                      <ActivationV2Gate>
                        {skipSidebar ? (
                          children
                        ) : (
                          <SidebarProvider
                            style={
                              {
                                '--sidebar-width': SIDEBAR_WIDTH,
                                '--sidebar-width-icon': '4rem'
                              } as CSSProperties
                            }
                          >
                            <AuthenticatedAppSidebar />
                            {/* This is the single shared shell for all top-level
                              pages (Matters, Scribe, Intake, Templates) via
                              LayoutWrapper (LF-5279). Bound the height and
                              neutralise the sidebar's `variant="inset"` chrome
                              (`m-2`/`rounded-xl`/`shadow-sm`) so pages render
                              flush and full-height. Without this the inset
                              margin pushes the inset past the viewport so the
                              *window* scrolls (native bar) instead of the inner
                              shared ScrollArea, and the rounded card frames the
                              content as an inset panel rather than flush. */}
                            <SidebarInset className="!m-0 h-screen min-w-0 overflow-hidden !rounded-none !shadow-none">
                              <LayoutWrapper>{children}</LayoutWrapper>
                            </SidebarInset>
                          </SidebarProvider>
                        )}
                      </ActivationV2Gate>
                      <CorrespondenceWorkflowWrapper />
                      <IntercomMessenger />
                    </CTADialogProvider>
                  </CreateMatterProvider>
                </NavigationLoadingProvider>
              </UserProvider>
            </ValeThemeProvider>
            <DevFeatureFlagToggle />
          </FeatureFlagsProvider>
        </UserProfileProvider>
      )}
      {/* Recording components live outside the conditional branches so they
          survive navigation between route types (dashboard ↔ sidebar pages) */}
      {!isPublicPath && (
        <>
          <UploadRecovery />
          <RecordingCompletionToaster />
        </>
      )}
    </QueryProvider>
  );
}
